import { useEffect, useMemo, useRef, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell, Stat } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Phone, FileText, Trash2, PhoneCall, BarChart3, Plus, Download, UserPlus } from 'lucide-react';
import CallLogDialog from '@/components/CallLogDialog';
import { useAuth } from '@/context/AuthContext';

const OUTCOME_LABEL = { connected: 'Connected', not_answered: 'Not Answered', callback: 'Callback', not_interested: 'Not Interested' };
const OUTCOME_COLOR = {
  connected: 'text-emerald-600 border-emerald-500/40 bg-emerald-500/10',
  not_answered: 'text-amber-600 border-amber-500/40 bg-amber-500/10',
  callback: 'text-blue-600 border-blue-500/40 bg-blue-500/10',
  not_interested: 'text-red-600 border-red-500/40 bg-red-500/10',
};
const SOURCE_LABEL = { cv_upload: 'CV Parsed', manual: 'Manual Entry' };
const SOURCE_COLOR = {
  cv_upload: 'text-primary border-primary/40 bg-primary/10',
  manual: 'text-violet-600 border-violet-500/40 bg-violet-500/10',
};
function SourceBadge({ s }) {
  const key = s || 'cv_upload';
  return <Badge variant="outline" className={`uppercase text-[10px] tracking-widest ${SOURCE_COLOR[key]}`} data-testid={`cl-source-${key}`}>{SOURCE_LABEL[key]}</Badge>;
}

const iso = (d) => d.toISOString().slice(0, 10);
// IST calendar date (YYYY-MM-DD) for `now` shifted by offsetDays.
// SHRS runs on IST (UTC+5:30) — never use raw new Date().toISOString() for calendar dates.
function todayIST(offsetDays = 0) {
  const now = new Date();
  const istMs = now.getTime() + (now.getTimezoneOffset() + 330) * 60000;
  const ist = new Date(istMs);
  ist.setUTCDate(ist.getUTCDate() + offsetDays);
  return ist.toISOString().slice(0, 10);
}
function computeRange(key) {
  const today = todayIST();
  if (key === 'today') return { from: today, to: today };
  if (key === 'yesterday') {
    const y = todayIST(-1);
    return { from: y, to: y };
  }
  if (key === 'week') {
    // Monday of this week (IST) → today (IST)
    // JS Date returns weekday in local time — use IST-adjusted date to compute weekday.
    const now = new Date();
    const istMs = now.getTime() + (now.getTimezoneOffset() + 330) * 60000;
    const ist = new Date(istMs);
    const dow = ist.getUTCDay(); // 0=Sun ... 1=Mon (in IST)
    const diffToMon = (dow === 0 ? -6 : 1 - dow);
    return { from: todayIST(diffToMon), to: today };
  }
  return null; // 'custom' uses user-picked from/to
}

function fmt(iso2) { if (!iso2) return '—'; try { return new Date(iso2).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso2; } }
function secToMMSS(s) { s = Math.max(0, Math.floor(s || 0)); const m = Math.floor(s / 60); return `${m}m ${s % 60}s`; }
function isoToLocalInput(iso3) {
  if (!iso3) return '';
  const d = new Date(iso3);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) { return v ? new Date(v).toISOString() : ''; }

const EMPTY_MANUAL = { name: '', phone: '', email: '', current_role: '', current_company: '', location: '', notes: '', next_follow_up: '' };

const CHIPS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'custom', label: 'Custom' },
];

export default function CallLogging() {
  const { user } = useAuth();
  const canFilter = ['admin', 'manager'].includes(user?.role);
  const fileRef = useRef(null);

  // Unified date filter for candidates + reporting (default: today)
  const [rangeKey, setRangeKey] = useState('today');
  const initial = computeRange('today');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);

  // Call pool
  const [cands, setCands] = useState([]);
  const [uploading, setUploading] = useState({ done: 0, total: 0 });
  const [callFor, setCallFor] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState(EMPTY_MANUAL);

  // Reporting
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ per_recruiter: [], totals: {} });
  const [users, setUsers] = useState([]);
  const [rRec, setRRec] = useState('all');
  const [rOutcome, setROutcome] = useState('all');
  const [rName, setRName] = useState('');

  // Top-of-page today snapshot (always today, ignores filter)
  const [snap, setSnap] = useState({ total_calls: 0, connected: 0, cv_uploads: 0, manual_entries: 0 });

  const applyChip = (key) => {
    setRangeKey(key);
    const r = computeRange(key);
    if (r) { setFrom(r.from); setTo(r.to); }
    // custom keeps whatever the user has set
  };

  // If the user manually edits dates → switch key to "custom" (unless the edit exactly matches a chip)
  const onEditDate = (which, v) => {
    if (which === 'from') setFrom(v); else setTo(v);
    // Not switching key here explicitly; recompute check happens elsewhere.
    setRangeKey('custom');
  };

  const loadCands = async () => {
    try {
      const { data } = await api.get('/call-candidates', { params: { from_date: from, to_date: to } });
      setCands(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadLogs = async () => {
    try {
      const params = { from_date: from, to_date: to };
      if (rRec !== 'all') params.recruiter_id = rRec;
      if (rOutcome !== 'all') params.outcome = rOutcome;
      if (rName) params.candidate_name = rName;
      const statsParams = { from_date: from, to_date: to };
      if (rRec !== 'all') statsParams.recruiter_id = rRec;
      const [{ data: L }, { data: S }] = await Promise.all([
        api.get('/call-logs', { params }),
        api.get('/call-logs/stats', { params: statsParams }),
      ]);
      setLogs(L); setStats(S);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadSnap = async () => {
    try {
      const t = todayIST();
      const { data } = await api.get('/call-logs/stats', { params: { from_date: t, to_date: t } });
      setSnap({
        total_calls: data.totals?.total_calls || 0,
        connected: data.totals?.connected || 0,
        cv_uploads: data.totals?.cv_uploads || 0,
        manual_entries: data.totals?.manual_entries || 0,
      });
    } catch { /* silent */ }
  };

  useEffect(() => { loadCands(); loadLogs(); /* eslint-disable-next-line */ }, [from, to, rRec, rOutcome, rName]);
  useEffect(() => { loadSnap(); }, []);
  useEffect(() => { if (canFilter) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {}); }, [canFilter]);

  // Auto-refresh today snapshot every 60s so it stays live and rolls over cleanly at midnight.
  useEffect(() => {
    const t = setInterval(loadSnap, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading({ done: 0, total: files.length });
    let done = 0;
    let fail = 0;
    let archiveWarnings = 0;
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append('file', f);
        const { data } = await api.post('/call-candidates/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.storage_warning) archiveWarnings += 1;
        done += 1;
      } catch (e) { fail += 1; console.error(e); }
      setUploading({ done, total: files.length });
    }
    setUploading({ done: 0, total: 0 });
    if (fail) {
      toast.error(`${done} uploaded · ${fail} failed`);
    } else if (archiveWarnings) {
      toast.warning(`${done} CV(s) parsed; ${archiveWarnings} file archive(s) unavailable.`);
    } else {
      toast.success(`Uploaded & parsed ${done} CV(s)`);
    }
    loadCands(); loadSnap();
  };

  const saveManual = async () => {
    if (!manual.name.trim()) { toast.error('Name is required'); return; }
    try {
      await api.post('/call-candidates', manual);
      toast.success('Candidate added');
      setManualOpen(false); setManual(EMPTY_MANUAL); loadCands(); loadSnap();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => { if (!window.confirm('Delete this candidate?')) return; try { await api.delete(`/call-candidates/${id}`); toast.success('Deleted'); loadCands(); } catch (e) { toast.error(formatErr(e)); } };

  const downloadCv = async (c) => {
    if (!c.resume_path) return;
    try {
      const res = await api.get(`/files/${c.resume_path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = c.resume_filename || 'resume';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setFollowUp = async (c, isoDT) => {
    try {
      await api.patch(`/call-candidates/${c.id}`, { ...c, next_follow_up: isoDT || null });
      toast.success('Follow-up set'); loadCands();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openCall = (c) => { setCallFor({ id: c.id, name: c.name, phone: c.phone }); setCallOpen(true); };

  const exportReport = async (fmtKey) => {
    try {
      const params = new URLSearchParams({ fmt: fmtKey, from_date: from, to_date: to });
      if (rRec !== 'all') params.set('recruiter_id', rRec);
      const res = await api.get(`/call-logs/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `shrs-call-logs-${from}_${to}.${fmtKey}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmtKey.toUpperCase()}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totals = stats.totals || {};
  const rangeLabel = useMemo(() => {
    if (rangeKey === 'today') return "Today";
    if (rangeKey === 'yesterday') return "Yesterday";
    if (rangeKey === 'week') return "This Week (Mon → today)";
    return `${from} → ${to}`;
  }, [rangeKey, from, to]);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Prospecting"
        title="Call Logging"
        subtitle="Bulk-upload CVs, add contacts manually, tap Call to dial, and log every conversation."
      />

      {/* Today snapshot — always today, resets naturally at midnight */}
      <div className="tactical-card p-4 sm:p-5 mb-6 border-primary/30 bg-primary/5" data-testid="cl-today-snapshot">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Live · resets each morning</div>
            <div className="font-display text-xl sm:text-2xl font-black tracking-tight">Today's snapshot · all recruiters</div>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-widest text-primary border-primary/40 bg-primary/10">
            {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="cl-snap-calls" label="Calls today" value={snap.total_calls} hint="Team-wide" accent />
          <Stat testid="cl-snap-connected" label="Connected" value={snap.connected} hint="Actual conversations" />
          <Stat testid="cl-snap-cv" label="CVs parsed" value={snap.cv_uploads} hint="Uploaded today" />
          <Stat testid="cl-snap-manual" label="Manual entries" value={snap.manual_entries} hint="Added today" />
        </div>
      </div>

      <Tabs defaultValue="pool" className="w-full">
        <TabsList data-testid="cl-tabs" className="mb-4 w-full sm:w-auto grid grid-cols-2 sm:inline-flex">
          <TabsTrigger value="pool" data-testid="cl-tab-pool"><PhoneCall className="w-4 h-4 mr-1" />Call pool</TabsTrigger>
          <TabsTrigger value="reports" data-testid="cl-tab-reports"><BarChart3 className="w-4 h-4 mr-1" />Reporting</TabsTrigger>
        </TabsList>

        {/* Shared quick filter chips — applies to both tabs */}
        <div className="tactical-card p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex flex-wrap gap-2" data-testid="cl-quick-chips">
            {CHIPS.map(c => (
              <button
                key={c.key}
                onClick={() => applyChip(c.key)}
                data-testid={`cl-chip-${c.key}`}
                className={`px-3 h-9 rounded-md text-xs uppercase tracking-widest font-black transition-colors border ${rangeKey === c.key ? 'bg-primary text-white border-transparent' : 'border-border hover:border-primary/50 text-foreground'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {rangeKey === 'custom' && (
            <div className="flex items-center gap-2" data-testid="cl-custom-dates">
              <Input type="date" value={from} onChange={(e) => onEditDate('from', e.target.value)} data-testid="cl-from" className="w-40 h-9" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={to} onChange={(e) => onEditDate('to', e.target.value)} data-testid="cl-to" className="w-40 h-9" />
            </div>
          )}
          <div className="sm:ml-auto text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="cl-range-label">Showing: {rangeLabel}</div>
        </div>

        <TabsContent value="pool" className="space-y-4">
          <div className="tactical-card p-4 space-y-3">
            <input ref={fileRef} type="file" data-testid="cl-file-input" multiple accept=".pdf,.doc,.docx,.txt,.rtf" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button onClick={() => fileRef.current?.click()} data-testid="cl-upload-btn" className="h-12 bg-primary hover:bg-primary/90 text-white">
                <Upload className="w-4 h-4 mr-2" />
                {uploading.total > 0 ? `Uploading ${uploading.done}/${uploading.total}…` : 'Upload CVs'}
              </Button>
              <Dialog open={manualOpen} onOpenChange={(v) => { setManualOpen(v); if (!v) setManual(EMPTY_MANUAL); }}>
                <DialogTrigger asChild>
                  <Button data-testid="cl-manual-btn" variant="outline" className="h-12 border-violet-500/40 text-violet-600 hover:bg-violet-500/10">
                    <UserPlus className="w-4 h-4 mr-2" /> Manual Entry
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-lg h-[92vh] sm:h-auto sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader>
                    <DialogTitle className="font-display text-xl sm:text-2xl font-black flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-violet-600" /> Add candidate manually
                    </DialogTitle>
                    <div className="text-xs text-muted-foreground mt-1">No CV needed — tag <span className="text-violet-600 font-black">Manual Entry</span> will be applied automatically.</div>
                  </DialogHeader>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2"><Label>Name *</Label><Input data-testid="cl-manual-name" value={manual.name} onChange={(e) => setManual(m => ({ ...m, name: e.target.value }))} className="h-11" /></div>
                    <div><Label>Phone</Label><Input data-testid="cl-manual-phone" type="tel" inputMode="tel" value={manual.phone} onChange={(e) => setManual(m => ({ ...m, phone: e.target.value }))} className="h-11" placeholder="+91…" /></div>
                    <div><Label>Email</Label><Input data-testid="cl-manual-email" type="email" inputMode="email" value={manual.email} onChange={(e) => setManual(m => ({ ...m, email: e.target.value }))} className="h-11" /></div>
                    <div><Label>Current role</Label><Input data-testid="cl-manual-role" value={manual.current_role} onChange={(e) => setManual(m => ({ ...m, current_role: e.target.value }))} className="h-11" /></div>
                    <div><Label>Current company</Label><Input value={manual.current_company} onChange={(e) => setManual(m => ({ ...m, current_company: e.target.value }))} className="h-11" /></div>
                    <div className="sm:col-span-2"><Label>Location</Label><Input value={manual.location} onChange={(e) => setManual(m => ({ ...m, location: e.target.value }))} className="h-11" /></div>
                    <div className="sm:col-span-2"><Label>Next follow-up</Label><Input type="datetime-local" data-testid="cl-manual-followup" value={isoToLocalInput(manual.next_follow_up)} onChange={(e) => setManual(m => ({ ...m, next_follow_up: localInputToIso(e.target.value) }))} className="h-11" /></div>
                    <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={3} data-testid="cl-manual-notes" value={manual.notes} onChange={(e) => setManual(m => ({ ...m, notes: e.target.value }))} placeholder="Referral, prior conversation, source…" /></div>
                  </div>
                  <div className="sticky bottom-0 bg-card pt-3 -mx-4 sm:mx-0 px-4 sm:px-0">
                    <Button onClick={saveManual} data-testid="cl-manual-save" className="w-full h-12 bg-violet-600 hover:bg-violet-700 text-white"><Plus className="w-4 h-4 mr-2" />Save candidate</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* MOBILE cards */}
          <div className="md:hidden space-y-3" data-testid="cl-cand-cards">
            {cands.length === 0 && <div className="tactical-card p-6 text-center text-sm text-muted-foreground">No candidates in this window. Try a wider filter or upload some CVs.</div>}
            {cands.map(c => {
              const follow = c.next_follow_up ? new Date(c.next_follow_up) : null;
              const overdue = follow && follow < new Date();
              return (
                <div key={c.id} className="tactical-card p-4" data-testid={`cl-cand-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-lg font-black tracking-tight truncate">{c.name}</div>
                        <SourceBadge s={c.source} />
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{c.current_role || c.current_company || '—'}</div>
                    </div>
                    {c.resume_path && (
                      <Button variant="ghost" size="sm" onClick={() => downloadCv(c)} className="flex-shrink-0" data-testid={`cl-cv-${c.id}`}>
                        <FileText className="w-4 h-4 mr-1" />CV
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-[11px] mb-3">
                    {c.phone && <div className="flex items-center gap-2"><span className="uppercase tracking-widest text-muted-foreground w-14">Phone</span><a href={`tel:${c.phone}`} className="text-primary font-mono">{c.phone}</a></div>}
                    {c.email && <div className="flex items-center gap-2"><span className="uppercase tracking-widest text-muted-foreground w-14">Email</span><span className="truncate">{c.email}</span></div>}
                    <div className="flex items-center gap-2"><span className="uppercase tracking-widest text-muted-foreground w-14">Added</span><span>{fmt(c.created_at)}</span></div>
                  </div>
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center justify-between">
                      <span>Next follow-up</span>
                      {follow && <Badge variant="outline" className={overdue ? 'text-red-600 border-red-500/40 bg-red-500/10' : 'text-blue-600 border-blue-500/40 bg-blue-500/10'}>{overdue ? 'overdue' : 'scheduled'}</Badge>}
                    </div>
                    <Input type="datetime-local" className="h-11" data-testid={`cl-cand-followup-${c.id}`}
                      value={isoToLocalInput(c.next_follow_up)}
                      onChange={(e) => setFollowUp(c, localInputToIso(e.target.value))} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => openCall(c)} data-testid={`cl-call-${c.id}`} className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white"><Phone className="w-4 h-4 mr-2" />Call &amp; log</Button>
                    <Button variant="outline" onClick={() => del(c.id)} className="h-12 text-red-500" data-testid={`cl-del-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP table */}
          <div className="hidden md:block tactical-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Current role</TableHead>
                  <TableHead>Next follow-up</TableHead>
                  <TableHead>Uploaded by</TableHead>
                  <TableHead>CV</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="cl-cand-tbody">
                {cands.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No candidates in this window.</TableCell></TableRow>}
                {cands.map(c => (
                  <TableRow key={c.id} data-testid={`cl-cand-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{fmt(c.created_at)}</div>
                    </TableCell>
                    <TableCell><SourceBadge s={c.source} /></TableCell>
                    <TableCell>{c.phone ? <a href={`tel:${c.phone}`} className="text-primary hover:underline">{c.phone}</a> : '—'}</TableCell>
                    <TableCell className="text-sm">{c.email || '—'}</TableCell>
                    <TableCell className="text-sm">{c.current_role || '—'}<div className="text-xs text-muted-foreground">{c.current_company || ''}</div></TableCell>
                    <TableCell>
                      <Input type="datetime-local" className="w-48 h-8 text-xs"
                        value={isoToLocalInput(c.next_follow_up)}
                        onChange={(e) => setFollowUp(c, localInputToIso(e.target.value))} />
                    </TableCell>
                    <TableCell className="text-sm">{c.uploaded_by_name}</TableCell>
                    <TableCell>
                      {c.resume_path ? <Button variant="ghost" size="sm" onClick={() => downloadCv(c)}><FileText className="w-3.5 h-3.5 mr-1" />CV</Button> : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" onClick={() => openCall(c)}><Phone className="w-3.5 h-3.5 mr-1" />Call</Button>
                      <Button variant="ghost" size="sm" onClick={() => del(c.id)} className="text-red-500 ml-1"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <div className="tactical-card p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {canFilter && (
              <div><Label className="text-[10px] uppercase tracking-widest">Recruiter</Label>
                <Select value={rRec} onValueChange={setRRec}><SelectTrigger className="h-11" data-testid="cl-r-recruiter"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All recruiters</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-[10px] uppercase tracking-widest">Outcome</Label>
              <Select value={rOutcome} onValueChange={setROutcome}><SelectTrigger className="h-11" data-testid="cl-r-outcome"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All outcomes</SelectItem>{Object.entries(OUTCOME_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest">Candidate</Label><Input value={rName} onChange={(e) => setRName(e.target.value)} data-testid="cl-r-name" placeholder="Name…" className="h-11" /></div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => exportReport('csv')} data-testid="cl-export-csv" className="h-11"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={() => exportReport('xlsx')} data-testid="cl-export-xlsx" className="h-11 bg-primary hover:bg-primary/90 text-white"><Download className="w-4 h-4 mr-2" />Export Excel</Button>
            <div className="ml-auto text-[11px] uppercase tracking-widest text-muted-foreground self-center">Export uses current filter</div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat testid="cl-stat-candidates" label="Candidates added" value={totals.candidates_added ?? 0} hint={`${totals.cv_uploads ?? 0} CV · ${totals.manual_entries ?? 0} manual`} accent />
            <Stat testid="cl-stat-total" label="Total calls" value={totals.total_calls ?? 0} hint={rangeLabel} />
            <Stat testid="cl-stat-connected" label="Connected" value={totals.connected ?? 0} hint="Talked" />
            <Stat testid="cl-stat-avg" label="Avg duration" value={secToMMSS(totals.avg_seconds ?? 0)} hint="Per call" />
          </div>

          <div className="tactical-card p-4 sm:p-6 overflow-x-auto">
            <div className="font-display text-lg font-black tracking-tight mb-3">Per recruiter breakdown</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Cand.</TableHead>
                  <TableHead className="hidden sm:table-cell">CV</TableHead>
                  <TableHead className="hidden sm:table-cell">Manual</TableHead>
                  <TableHead>Calls</TableHead>
                  <TableHead className="hidden sm:table-cell">Conn.</TableHead>
                  <TableHead className="hidden md:table-cell">Not Ans.</TableHead>
                  <TableHead className="hidden md:table-cell">Cb.</TableHead>
                  <TableHead className="hidden md:table-cell">N.Int.</TableHead>
                  <TableHead>Avg</TableHead>
                  <TableHead className="hidden sm:table-cell">Conv. %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="cl-r-per-rec">
                {stats.per_recruiter.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">No activity in this range.</TableCell></TableRow>}
                {stats.per_recruiter.map(r => (
                  <TableRow key={r.recruiter_id} data-testid={`cl-r-row-${r.recruiter_id}`}>
                    <TableCell className="font-medium">{r.recruiter_name}</TableCell>
                    <TableCell className="font-mono font-black">{r.candidates_added}</TableCell>
                    <TableCell className="text-primary font-mono hidden sm:table-cell">{r.cv_uploads}</TableCell>
                    <TableCell className="text-violet-600 font-mono hidden sm:table-cell">{r.manual_entries}</TableCell>
                    <TableCell className="font-mono font-black">{r.total_calls}</TableCell>
                    <TableCell className="text-emerald-600 font-mono hidden sm:table-cell">{r.connected}</TableCell>
                    <TableCell className="text-amber-600 font-mono hidden md:table-cell">{r.not_answered}</TableCell>
                    <TableCell className="text-blue-600 font-mono hidden md:table-cell">{r.callback}</TableCell>
                    <TableCell className="text-red-600 font-mono hidden md:table-cell">{r.not_interested}</TableCell>
                    <TableCell className="font-mono">{secToMMSS(r.avg_seconds)}</TableCell>
                    <TableCell className="hidden sm:table-cell" data-testid={`cl-conv-${r.recruiter_id}`}>
                      <span className="font-mono font-black">{r.conversion_pct != null ? `${r.conversion_pct}%` : '—'}</span>
                      <span className="text-[10px] text-muted-foreground ml-1">({r.lineups_interview || 0}/{r.lineups_total || 0})</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="tactical-card p-4 sm:p-6">
            <div className="font-display text-lg font-black tracking-tight mb-3">Call log · {logs.length} entries</div>

            <div className="md:hidden space-y-3">
              {logs.length === 0 && <div className="text-center text-muted-foreground py-6 text-sm">No calls logged in this range.</div>}
              {logs.map(l => (
                <div key={l.id} data-testid={`cl-log-${l.id}`} className="border border-border/70 rounded-md p-3 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{l.candidate_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{l.candidate_phone}</div>
                    </div>
                    <Badge variant="outline" className={OUTCOME_COLOR[l.outcome]}>{OUTCOME_LABEL[l.outcome] || l.outcome}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center justify-between">
                    <span>{fmt(l.started_at)} → {fmt(l.ended_at)}</span>
                    <span className="font-mono font-black text-foreground">{secToMMSS(l.duration_seconds)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">by {l.recruiter_name}</div>
                  {l.notes && <div className="text-xs pt-1 border-t border-border/40 mt-1">{l.notes}</div>}
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Recruiter</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Ended</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="cl-r-logs">
                  {logs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No calls logged in this range.</TableCell></TableRow>}
                  {logs.map(l => (
                    <TableRow key={l.id}>
                      <TableCell>
                        <div className="font-medium">{l.candidate_name}</div>
                        <div className="text-xs text-muted-foreground">{l.candidate_phone}</div>
                      </TableCell>
                      <TableCell className="text-sm">{l.recruiter_name}</TableCell>
                      <TableCell className="text-xs">{fmt(l.started_at)}</TableCell>
                      <TableCell className="text-xs">{fmt(l.ended_at)}</TableCell>
                      <TableCell className="font-mono">{secToMMSS(l.duration_seconds)}</TableCell>
                      <TableCell><Badge variant="outline" className={OUTCOME_COLOR[l.outcome]}>{OUTCOME_LABEL[l.outcome] || l.outcome}</Badge></TableCell>
                      <TableCell className="text-xs max-w-[240px] truncate" title={l.notes}>{l.notes || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <CallLogDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        candidate={callFor}
        candidateType="call_candidate"
        defaultDialer
        onSaved={() => { setCallOpen(false); loadLogs(); loadSnap(); }}
      />
    </PageShell>
  );
}
